import { useEffect, useMemo, useRef, useState } from "react";
import type { Marker, MarkerKind, Play, Position } from "../types";
import { cloneStep, limitPlayerName, PLAYER_COLOR_PALETTE, playerLabel, sanitizePlayForSave, updatePlayerProfile } from "../lib/play";
import { removeDraft, saveDraft } from "../lib/drafts";
import { useHistory } from "../hooks/useHistory";
import { CourtBoard } from "./CourtBoard";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Redo2, Save, Trash2, Undo2 } from "./icons";

type Tool = "select" | "ball" | MarkerKind;

interface EditorProps {
  initialPlay: Play;
  initialDirty?: boolean;
  onBack: () => void;
  onSave: (play: Play) => Promise<Play>;
}

function signature(play: Play) {
  return JSON.stringify({ name: play.name, description: play.description, courtType: play.courtType, steps: play.steps, version: play.version });
}

export function Editor({ initialPlay, initialDirty = false, onBack, onSave }: EditorProps) {
  const history = useHistory(initialPlay);
  const play = history.value;
  const [stepIndex, setStepIndex] = useState(0);
  const [tool, setTool] = useState<Tool>("select");
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [draftTime, setDraftTime] = useState<Date | null>(null);
  const [cloudSavedAt, setCloudSavedAt] = useState<Date | null>(initialPlay.version > 0 && !initialDirty ? new Date(initialPlay.updatedAt) : null);
  const savedSignature = useRef(initialDirty ? "" : signature(initialPlay));
  const currentStep = play.steps[stepIndex];
  const dirty = play.version === 0 || signature(play) !== savedSignature.current;

  const updateStep = (updater: (step: Play["steps"][number]) => Play["steps"][number]) => {
    history.commit((current) => ({
      ...current,
      steps: current.steps.map((step, index) => index === stepIndex ? updater(structuredClone(step)) : step),
    }));
  };

  useEffect(() => {
    if (!dirty) {
      removeDraft(play.id);
      return;
    }
    const timer = window.setTimeout(() => {
      saveDraft(play);
      setDraftTime(new Date());
    }, 500);
    return () => window.clearTimeout(timer);
  }, [dirty, play]);

  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener("beforeunload", listener);
    return () => window.removeEventListener("beforeunload", listener);
  }, [dirty]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        event.shiftKey ? history.redo() : history.undo();
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedMarker && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
        event.preventDefault();
        updateStep((step) => ({ ...step, markers: step.markers.filter((marker) => marker.id !== selectedMarker) }));
        setSelectedMarker(null);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });

  const tools: { value: Tool; label: string; glyph: string }[] = [
    { value: "select", label: "选择 / 移动", glyph: "↖" },
    { value: "run", label: "跑动箭头", glyph: "➜" },
    { value: "pass", label: "传球虚线", glyph: "⇢" },
    { value: "screen", label: "掩护标记", glyph: "⊥" },
    { value: "ball", label: "指定持球人", glyph: "●" },
  ];

  const defenseVisible = useMemo(() => currentStep.players.some((player) => player.side === "defense" && player.visible), [currentStep]);
  const selectedOffensePlayer = useMemo(
    () => currentStep.players.find((player) => player.id === selectedPlayer && player.side === "offense") ?? null,
    [currentStep, selectedPlayer],
  );

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const saved = await onSave(sanitizePlayForSave(play));
      history.reset(saved);
      savedSignature.current = signature(saved);
      removeDraft(saved.id);
      setDraftTime(null);
      setCloudSavedAt(new Date(saved.updatedAt));
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : "保存失败，草稿仍保留在本机");
    } finally {
      setSaving(false);
    }
  };

  const requestBack = () => {
    if (!dirty || window.confirm("修改尚未保存到战术库。本机草稿会保留，确定返回吗？")) onBack();
  };

  return (
    <main className="editor-page">
      <header className="editor-header">
        <button className="text-button" onClick={requestBack}><ArrowLeft size={18} /> 战术库</button>
        <div className="editor-name">
          <input value={play.name} maxLength={80} onChange={(event) => history.commit((current) => ({ ...current, name: event.target.value }))} aria-label="战术名称" />
          <span>{dirty
            ? draftTime ? `草稿已保留 ${draftTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "正在保留草稿…"
            : `已保存到战术库${cloudSavedAt ? ` ${cloudSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`}</span>
        </div>
        <div className="header-actions">
          <button className="icon-button" onClick={history.undo} disabled={!history.canUndo} aria-label="撤销"><Undo2 size={18} /></button>
          <button className="icon-button" onClick={history.redo} disabled={!history.canRedo} aria-label="重做"><Redo2 size={18} /></button>
          <button className="primary" onClick={save} disabled={saving || !dirty}><Save size={17} /> {saving ? "保存中…" : "保存到战术库"}</button>
        </div>
      </header>
      {saveError && <div className="save-error" role="alert">{saveError} <button onClick={save}>重试</button></div>}

      <div className="editor-workspace">
        <aside className="tool-panel">
          <p className="panel-label">工具</p>
          {tools.map((item) => (
            <button key={item.value} className={tool === item.value ? "active" : ""} onClick={() => { setTool(item.value); setSelectedMarker(null); if (item.value !== "select") setSelectedPlayer(null); }}>
              <span>{item.glyph}</span>{item.label}
            </button>
          ))}
          <div className="tool-divider" />
          <label className="switch-row">
            <span>显示防守</span>
            <input type="checkbox" checked={defenseVisible} onChange={(event) => {
              const visible = event.target.checked;
              updateStep((step) => ({ ...step, players: step.players.map((player) => player.side === "defense" ? { ...player, visible } : player) }));
            }} />
          </label>
          {selectedMarker && (
            <button className="danger subtle" onClick={() => {
              updateStep((step) => ({ ...step, markers: step.markers.filter((marker) => marker.id !== selectedMarker) }));
              setSelectedMarker(null);
            }}><Trash2 size={16} /> 删除标记</button>
          )}
        </aside>

        <section className="editor-canvas">
          <div className="canvas-hint">
            {tool === "select" && "点击进攻球员设置姓名与颜色，拖动可调整站位"}
            {tool === "ball" && "点击一名进攻球员指定持球人"}
            {tool === "run" && "在球场上拖动，绘制跑动箭头"}
            {tool === "pass" && "在球场上拖动，绘制传球虚线"}
            {tool === "screen" && "在球场上拖动，绘制掩护标记"}
          </div>
          <CourtBoard
            step={currentStep}
            courtType={play.courtType}
            interactive
            tool={tool}
            selectedMarkerId={selectedMarker}
            selectedPlayerId={selectedPlayer}
            onPlayerMove={(id: string, position: Position) => updateStep((step) => ({ ...step, players: step.players.map((player) => player.id === id ? { ...player, ...position } : player) }))}
            onPlayerSelect={(id) => { setSelectedPlayer(id); if (id) setSelectedMarker(null); }}
            onBallMove={(position) => updateStep((step) => ({ ...step, ballOwnerId: null, ball: position }))}
            onBallOwnerChange={(id) => updateStep((step) => ({ ...step, ballOwnerId: id }))}
            onMarkerAdd={(marker: Marker) => updateStep((step) => ({ ...step, markers: [...step.markers, marker] }))}
            onMarkerSelect={(id) => { setSelectedMarker(id); setSelectedPlayer(null); }}
          />
        </section>

        <aside className="step-panel">
          <p className="panel-label">步骤 {stepIndex + 1} / {play.steps.length}</p>
          {selectedOffensePlayer && (
            <section className="player-profile-editor" aria-label={`设置 ${selectedOffensePlayer.number} 号进攻球员`}>
              <div className="player-profile-heading">
                <span style={{ backgroundColor: selectedOffensePlayer.color ?? "#10251a", color: selectedOffensePlayer.color ? "#102014" : "#eaffc2" }}>{playerLabel(selectedOffensePlayer)}</span>
                <div><strong>{selectedOffensePlayer.number} 号进攻球员</strong><small>姓名和颜色将同步到所有步骤</small></div>
              </div>
              <label>球员名字
                <input
                  value={selectedOffensePlayer.name ?? ""}
                  placeholder={`${selectedOffensePlayer.number} 号`}
                  onChange={(event) => {
                    const name = limitPlayerName(event.target.value);
                    history.commit((current) => updatePlayerProfile(current, selectedOffensePlayer.id, { name, color: selectedOffensePlayer.color }));
                  }}
                />
              </label>
              <fieldset>
                <legend>图标颜色</legend>
                <div className="player-color-palette">
                  {PLAYER_COLOR_PALETTE.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={selectedOffensePlayer.color === color.value ? "active" : ""}
                      style={{ backgroundColor: color.value }}
                      aria-label={color.label}
                      aria-pressed={selectedOffensePlayer.color === color.value}
                      title={color.label}
                      onClick={() => history.commit((current) => updatePlayerProfile(current, selectedOffensePlayer.id, { name: selectedOffensePlayer.name, color: color.value }))}
                    />
                  ))}
                </div>
              </fieldset>
            </section>
          )}
          <label>步骤标题<input value={currentStep.title} maxLength={80} onChange={(event) => updateStep((step) => ({ ...step, title: event.target.value }))} /></label>
          <label>讲解<textarea value={currentStep.note} maxLength={1000} placeholder="写下跑位重点和提醒…" onChange={(event) => updateStep((step) => ({ ...step, note: event.target.value }))} /></label>
          <label>动画时长<div className="duration-input"><input type="range" min="0.3" max="5" step="0.1" value={currentStep.duration} onChange={(event) => updateStep((step) => ({ ...step, duration: Number(event.target.value) }))} /><span>{currentStep.duration.toFixed(1)} 秒</span></div></label>
          <label>战术说明<textarea value={play.description} maxLength={1000} placeholder="这套战术适合何时使用？" onChange={(event) => history.commit((current) => ({ ...current, description: event.target.value }))} /></label>
          <div className="step-nav">
            <button className="icon-button" onClick={() => setStepIndex((value) => value - 1)} disabled={stepIndex === 0}><ChevronLeft size={20} /></button>
            <button className="icon-button" onClick={() => setStepIndex((value) => value + 1)} disabled={stepIndex >= play.steps.length - 1}><ChevronRight size={20} /></button>
          </div>
        </aside>
      </div>

      <footer className="timeline">
        <div className="timeline-scroll">
          {play.steps.map((step, index) => (
            <button key={step.id} className={`timeline-step ${index === stepIndex ? "active" : ""}`} onClick={() => { setStepIndex(index); setSelectedMarker(null); }}>
              <span>{index + 1}</span><strong>{step.title}</strong><small>{step.duration.toFixed(1)}s</small>
            </button>
          ))}
          <button className="add-step" onClick={() => {
            const next = cloneStep(currentStep);
            next.title = `第 ${stepIndex + 2} 步`;
            history.commit((current) => ({ ...current, steps: [...current.steps.slice(0, stepIndex + 1), next, ...current.steps.slice(stepIndex + 1)] }));
            setStepIndex(stepIndex + 1);
          }}><Plus size={18} /> 添加下一步</button>
        </div>
        <button className="delete-step" disabled={play.steps.length === 1} onClick={() => {
          history.commit((current) => ({ ...current, steps: current.steps.filter((_, index) => index !== stepIndex) }));
          setStepIndex(Math.max(0, stepIndex - 1));
        }}><Trash2 size={16} /> 删除当前步骤</button>
      </footer>
    </main>
  );
}
